import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getKidSub } from '@/auth/authStore';

import type { StudyDocument } from './studyApi';
import { analysisPath, quoteStudyAnalysis } from './studyAnalysisApi';
import { ANALYSIS_NOT_FOR_SCHOOL, analysisUnavailable } from './studyAnalysisErrors';
import { useStudyAvailability } from './studyAvailability';
import { StudyCaptureFlow } from './StudyCaptureFlow';
import { materialProgressLine } from './studyMaterialRow';

/**
 * The analysis line on a material card (owner feedback 2026-10-08): question progress for an
 * analysed material ("2 questions · 0 done"), or a small "Analyse · N★" button for one that is
 * not. The price comes from the server quote (same query as Home's progress chip); the button
 * opens the existing capture flow, whose confirm step takes the Stars. A school account never
 * asks for a price and sees the button off (study-capture-analyse-prd.md §6).
 */

function isAnalysable(mimeType: string) {
  return mimeType === 'application/pdf' || mimeType.startsWith('image/');
}

export function StudyMaterialAnalyseEntry({
  document,
  title,
}: {
  document: StudyDocument;
  /** The card title, for the button name "Analyse <title> · N★". */
  title: string;
}) {
  const navigate = useNavigate();
  const reasonId = useId();
  const [flowOpen, setFlowOpen] = useState(false);
  const availability = useStudyAvailability();
  const school = availability.isPending
    ? null
    : availability.data?.learner_source === 'institution' &&
      availability.data.paid_ai_funding !== 'institution_pool';
  const progress = materialProgressLine(document.analysis);
  const needsAnalysis = isAnalysable(document.mime_type) && !document.analysis;
  const isPdf = document.mime_type === 'application/pdf';
  const quote = useQuery({
    queryKey: ['study-analysis-quote', getKidSub(), `${document.id}:1`],
    queryFn: () => quoteStudyAnalysis([{ document_id: document.id, page_number: 1 }]),
    enabled: needsAnalysis && school === false,
    retry: false,
    staleTime: 30_000,
  });

  if (progress) return <p className="text-sm font-bold text-brand-sky">{progress}</p>;
  if (!needsAnalysis || school === null) return null;

  if (school || analysisUnavailable(quote.error)) {
    return (
      <p className="flex flex-wrap items-center gap-x-2 text-xs font-bold text-slate2">
        <button
          type="button"
          disabled
          aria-describedby={reasonId}
          className="min-h-11 text-sm font-bold text-slate2 opacity-60"
        >
          Analyse
        </button>
        <span id={reasonId}>{ANALYSIS_NOT_FOR_SCHOOL}</span>
      </p>
    );
  }

  const price = quote.data
    ? isPdf
      ? ` · ${quote.data.stars_per_page}★ a page`
      : ` · ${quote.data.stars_total}★`
    : '';
  return (
    <>
      <button
        type="button"
        onClick={() => setFlowOpen(true)}
        aria-label={`Analyse ${title}${price}`}
        className="inline-flex min-h-11 items-center rounded-full text-sm font-extrabold text-brand-sky underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky"
      >
        Analyse{price}
      </button>
      {flowOpen && (
        <StudyCaptureFlow
          source={{ kind: 'document', document }}
          onClose={() => setFlowOpen(false)}
          onSaved={() => setFlowOpen(false)}
          onAnalysed={(created) => navigate(analysisPath(created.primary_document_id, created.id))}
        />
      )}
    </>
  );
}
